import React, {useState, useEffect, useContext} from 'react';
import {useWindowDimensions, Platform} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import SplashScreen from 'react-native-splash-screen';

import {I18nContext} from 'i18n/context/I18nContext';
import AvatarPreviewPortrait from 'screens/AvatarPreview/AvatarPreviewPortrait';
import AvatarPreviewLandscape from 'screens/AvatarPreview/AvatarPreviewLandscape';
import {goToDeviceConfig} from 'store/app';
import World from 'components/World';

const SET_AVATAR_DATA = 'SET_AVATAR_DATA';

const AvatarPreview = () => {
  const dispatch = useDispatch();
  const {height, width} = useWindowDimensions();
  const isPortrait = height > width;

  const [loaded, setLoaded] = useState(false);

  const {translate} = useContext(I18nContext);

  // Web only: if the URL gave us an avatar (or a previous customizer save
  // already populated Redux), skip the customizer and load that avatar
  // directly. On native, avatarId is always empty here (no URL bar / no
  // postMessage listener) — so the else-branch fires and behavior is
  // unchanged.
  const persistedAvatarId = useSelector((state) => state.appearance.avatarId);
  const persistedSkinTone = useSelector(
    (state) => state.appearance.skinToneHex,
  );
  const peerId = useSelector((state) => state.communication.me.peerId);

  const onLoadComplete = () => {
    if (peerId) {
      World.runCommand(`set_external_user_id ${peerId}`);
    }
    if (persistedAvatarId) {
      // Encode AvatarInfo for Unity. Field names must match Unity's
      // AvatarInfo C# class exactly (avaTwinAvatarId, avaTwinSkinToneHex).
      // `local` is the user prefix consumed by AvatarManager — mirrors what
      // OpenCustomizer.cs does on the Unity side after a fresh save.
      const avatarInfoJson = JSON.stringify({
        avaTwinAvatarId: persistedAvatarId,
        avaTwinSkinToneHex: persistedSkinTone || null,
      });
      const encoded = global.btoa(
        unescape(encodeURIComponent(avatarInfoJson)),
      );
      console.log(
        '[WEBGL_DBG] AvatarPreview.onLoadComplete: skipping customizer, set_avatar local len=' +
          encoded.length,
      );
      // Unity will emit 'p|avatarReady' once the avatar finishes loading;
      // the existing onLegacyFromWorld listener catches it and dispatches
      // goToDeviceConfig().
      World.runCommand(`set_avatar local ${encoded}`);
    } else {
      // No persisted avatar — open the customizer as before.
      World.runCommand('open_customizer');
    }
    setLoaded(true);
  };

  const onLegacyFromWorld = (message) => {
    const {route, payload} = message;

    if (route === 'p' && payload) {
      const parts = payload.split('|');
      const variable = parts[0];

      if (variable === 'avatarReady' && parts.length >= 3) {
        const id = parts[1];
        const skin = parts[2];

        // Persist avatar selection in Redux for the Meeting screen.
        dispatch({type: SET_AVATAR_DATA, avatarId: id, skinToneHex: skin});

        // Save & Continue in the Ava-Twin customizer IS the "continue"
        // gesture — auto-navigate to DeviceConfig so the user doesn't need
        // to tap an intermediate RN Join button.
        dispatch(goToDeviceConfig());
      }
    }
    // ignore all other routes/variables
  };

  // Avoid flashing white background on app load
  useEffect(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      SplashScreen.hide();
    }
  }, []);

  const commonProps = {
    onLoadComplete,
    loaded,
    onLegacyFromWorld,
  };

  const display = isPortrait ? (
    <AvatarPreviewPortrait {...commonProps} />
  ) : (
    <AvatarPreviewLandscape {...commonProps} />
  );

  return display;
};

export default AvatarPreview;
